import { observer } from 'mobx-react';
import { useRef } from 'react';
import { useHistory } from 'react-router-dom';

// Components
import { DeleteOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';

// Hooks
import { useStores } from 'hooks/use-stores';

// Tracking
import { itemModalTrackingEvents } from 'integrations/segment/tracking-events';
import { cartFormatting } from 'integrations/segment/util/formatting';
import CartItem from 'models/CartItem';
import SharedCartItem from 'models/SharedCartItem';
import { Container, DeleteButton, Header, Qty, Row, UpdateButton } from './styles';

function EditQuantity({ cartItem, isNew }: { cartItem: CartItem | SharedCartItem; isNew: boolean }) {
  const history = useHistory();
  const { checkoutStore } = useStores();
  const initialHash = useRef(cartItem.hash());

  const handleDelete = () => {
    itemModalTrackingEvents.trackClickRemoveCartItem(cartFormatting.formatCartItemForTracking(cartItem));
    // Track initial hash to ensure the item can be deleted even if the user has changed item mods while editing.
    checkoutStore.selectedCart?.removeItem(cartItem, initialHash.current);
    history.goBack();
  };

  const handleDecreaseQty = () => {
    cartItem.setQty(cartItem.qty - 1);
    itemModalTrackingEvents.trackClickQuantityStepper({
      type: 'decrement',
      ...cartFormatting.formatCartItemForTracking(cartItem),
    });
  };

  const handleIncreaseQty = () => {
    cartItem.setQty(cartItem.qty + 1);
    itemModalTrackingEvents.trackClickQuantityStepper({
      type: 'increment',
      ...cartFormatting.formatCartItemForTracking(cartItem),
    });
  };

  if (!cartItem.menuItem.in_stock || !cartItem.menuItem.isFulfillable) {
    return null;
  }

  return (
    <Container data-testid="cart-item-edit-quantity">
      <Header>Quantity</Header>
      <Row>
        <UpdateButton
          aria-label="Decrease quantity"
          data-testid="decrement-qty"
          disabled={cartItem.qty < 2 || !checkoutStore.isAllowedToEditItem(cartItem)}
          icon={<MinusOutlined />}
          onClick={handleDecreaseQty}
          shape="circle"
          size="large"
        />
        <Qty data-testid="cart-item-qty-amount">{cartItem.qty}</Qty>
        <UpdateButton
          aria-label="Increase quantity"
          data-testid="increment-qty"
          disabled={
            cartItem.qty > 98 ||
            (cartItem.menuItem.inventory_qty !== null && cartItem.qty >= cartItem.menuItem.inventory_qty) ||
            !checkoutStore.isAllowedToEditItem(cartItem)
          }
          icon={<PlusOutlined />}
          onClick={handleIncreaseQty}
          shape="circle"
          size="large"
        />
      </Row>
      {!isNew && (
        <DeleteButton
          disabled={!checkoutStore.isAllowedToEditItem(cartItem)}
          data-testid="remove-cart-item"
          icon={<DeleteOutlined />}
          onClick={handleDelete}
        >
          Remove item{cartItem.qty > 1 ? 's' : ''}
        </DeleteButton>
      )}
    </Container>
  );
}

export default observer(EditQuantity);
